import type { JSX } from 'react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useEscape } from '../shell/use-escape';

/**
 * The design's dropdown (L463–481): a field-like button with a chevron, and a
 * floating list with a dot beside the chosen option. A native select cannot
 * be styled like this, so this is the one custom control.
 */
export function Dropdown({
  label,
  value,
  options,
  onChange,
  mono = false,
  disabled = false,
}: {
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  mono?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value)?.label ?? value;
  const font = mono ? 'font-mono' : '';

  return (
    <div className="text-mute relative flex flex-col gap-1.5 text-[13px]">
      {label}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn(
          'bg-surf2 text-ink hover:border-acc flex items-center justify-between gap-2.5 rounded-[9px] border px-3 py-2.5 text-left text-sm transition-[border-color] duration-200 disabled:opacity-70',
          open ? 'border-acc' : 'border-line',
          font,
        )}
      >
        <span className="min-w-0 truncate">{current}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-mute flex-none transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && <Menu options={options} value={value} font={font} onPick={pick} onClose={close} />}
    </div>
  );

  function pick(next: string): void {
    onChange(next);
    setOpen(false);
  }
  function close(): void {
    setOpen(false);
  }
}

function Menu({
  options,
  value,
  font,
  onPick,
  onClose,
}: {
  options: readonly { value: string; label: string }[];
  value: string;
  font: string;
  onPick: (value: string) => void;
  onClose: () => void;
}): JSX.Element {
  useEscape(onClose);
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-[25]" />
      <div
        role="listbox"
        className="bg-surf border-line shadow-panel absolute top-[calc(100%+6px)] right-0 left-0 z-[26] flex animate-[rise_.18s_ease_both] flex-col gap-0.5 rounded-[10px] border p-[5px]"
      >
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onPick(option.value)}
              className={cn(
                'hover:bg-surf2 flex items-center justify-between gap-2.5 rounded-[7px] border-0 px-2.5 py-[9px] text-left text-[13.5px]',
                selected ? 'bg-acc-soft text-acc' : 'text-ink bg-transparent',
                font,
              )}
            >
              <span>{option.label}</span>
              {selected && <span className="bg-acc size-1.5 flex-none rounded-full" />}
            </button>
          );
        })}
      </div>
    </>
  );
}
